import { useCallback, useMemo } from 'react';
import { Credentials } from '@app/shared/types';
import { useProjectEnvMap, ProjectEnvMap } from '../../../shared/hooks/useProjectEnvMap';

interface useProjectByEnvsProps {
  externalCredentials?: Credentials[];
  deletedCredentials: Set<string>;
  newCredentials: Credentials[];
}

interface useProjectByEnvsReturn {
  usedByProject: Record<string, Set<string>>;
  allCombosCount: number;
  usedCombosCount: number;
  noMoreAvailableEnvs: boolean;
}

export const useProjectByEnvs = ({
  externalCredentials = [],
  deletedCredentials,
  newCredentials,
}: useProjectByEnvsProps): useProjectByEnvsReturn => {
  const projectEnvMap = useProjectEnvMap();

  const parseProjectEnv = useCallback(
    (name: string, map: ProjectEnvMap): { project?: string; env?: string } => {
      if (!name) return {};
      const projects = Object.keys(map || {});

      for (const project of projects) {
        if (name.startsWith(`${project} `)) {
          const envCandidate = name.slice(project.length + 1);
          if ((map[project] || []).includes(envCandidate)) {
            return { project, env: envCandidate };
          }
        }
      }
      return {};
    },
    [],
  );

  const usedByProject: Record<string, Set<string>> = useMemo(() => {
    const result: Record<string, Set<string>> = {};

    const add = (cred?: Credentials) => {
      if (!cred?.name) return;
      const { project, env } = parseProjectEnv(cred.name, projectEnvMap);
      if (project && env) {
        if (!result[project]) result[project] = new Set<string>();
        result[project].add(env);
      }
    };

    externalCredentials
      .filter(cred => !deletedCredentials.has(cred.clientId))
      .forEach(add);

    newCredentials.forEach(add);
    return result;
  }, [
    externalCredentials,
    deletedCredentials,
    newCredentials,
    parseProjectEnv,
    projectEnvMap,
  ]);

  const allCombosCount = useMemo(
    () => Object.values(projectEnvMap || {}).reduce((acc, envs) => acc + envs.length, 0),
    [projectEnvMap],
  );

  const usedCombosCount = useMemo(() => {
    return Object.values(usedByProject).reduce((acc, set) => acc + set.size, 0);
  }, [usedByProject]);

  const noMoreAvailableEnvs = allCombosCount === 0 || usedCombosCount >= allCombosCount;

  return {
    usedByProject,
    allCombosCount,
    usedCombosCount,
    noMoreAvailableEnvs,
  };
};

